'use client'

import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { usePathname } from 'next/navigation'
import styles from './PageTitle.module.scss'
import { ComponentProps } from '@/types/database'
import { usePreloader } from '@/components/PagePreloader/PreloaderContext'
import { sanitizeBasicHtml } from '@/lib/utils/html-sanitizer'

interface PageTitleProps extends ComponentProps {
  title?: string
  description?: string
  headingLevel?: 'h1' | 'h2'
  alignment?: 'left' | 'center'
  extraLarge?: boolean
  noTopPadding?: boolean
  noBottomPadding?: boolean
}

export const PageTitle: React.FC<PageTitleProps> = ({
  title = 'Page Title',
  description = '',
  headingLevel = 'h1',
  alignment = 'left',
  extraLarge = false,
  noTopPadding = false,
  noBottomPadding = false
}) => {
  const HeadingTag = headingLevel
  
  // Animation states
  const [titleInView, setTitleInView] = useState(false)
  const [descriptionInView, setDescriptionInView] = useState(false)
  
  // Refs for intersection observers
  const titleRef = useRef<HTMLHeadingElement>(null)
  const descriptionRef = useRef<HTMLParagraphElement>(null)
  
  const pathname = usePathname()
  const isAdminMode = pathname.startsWith('/admin')
  const { state: preloaderState, isInitialLoad } = usePreloader()
  
  // Check if animations should be enabled
  const canAnimate = !isAdminMode && preloaderState === 'hidden' && !isInitialLoad

  // Title intersection observer
  useEffect(() => {
    const element = titleRef.current
    
    if (!element || isAdminMode) {
      if (isAdminMode) setTitleInView(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && canAnimate) {

          setTitleInView(true)
        }
      },
      { threshold: 0.3 }
    )

    observer.observe(element)
    return () => observer.unobserve(element)
  }, [canAnimate, isAdminMode])

  // Description intersection observer
  useEffect(() => {
    const element = descriptionRef.current
    
    if (!element || isAdminMode) {
      if (isAdminMode) setDescriptionInView(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && canAnimate) {

          setDescriptionInView(true)
        }
      },
      { threshold: 0.3 }
    )

    observer.observe(element)
    return () => observer.unobserve(element)
  }, [canAnimate, isAdminMode])
  
  const titleClasses = [
    styles['page-title__title'],
    styles[`page-title__title--${headingLevel}`]
  ].filter(Boolean).join(' ')

  const sectionClasses = [
    styles['page-title'],
    styles[`page-title--${alignment}`],
    extraLarge ? styles['page-title--xl'] : ''
  ].filter(Boolean).join(' ')

  return (
    <section className={sectionClasses}>
      <div className={`${styles['page-title__container']} ${
        noTopPadding && noBottomPadding ? styles['page-title__container--no-vertical'] :
        noTopPadding ? styles['page-title__container--no-top'] :
        noBottomPadding ? styles['page-title__container--no-bottom'] : ''
      }`}>
        <motion.div
          ref={titleRef}
          initial={{ opacity: isAdminMode ? 1 : 0 }}
          animate={{ opacity: titleInView ? 1 : 0 }}
          transition={{ 
            duration: 0.8,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
        >
          <HeadingTag 
            className={titleClasses}
            dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(title) }}
          />
        </motion.div>
        
        {description && (
          <motion.p 
            ref={descriptionRef}
            className={styles['page-title__description']}
            initial={{ opacity: isAdminMode ? 1 : 0 }}
            animate={{ opacity: descriptionInView ? 1 : 0 }}
            transition={{ 
              duration: 0.8,
              delay: 0.2, // Appears after title
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(description) }}
          />
        )}
      </div>
    </section>
  )
} 